import { Component } from '@theme/component';

const HEADING_PRESETS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];
const TYPOGRAPHY_STYLE = /font|line-height|letter-spacing/i;

/**
 * Only inline typography marks HTML as imported. Decorations like underline come from the merchant editor.
 * @param {HTMLElement} block
 */
function hasImportedTypography(block) {
  for (const element of block.querySelectorAll('[style]')) {
    if (TYPOGRAPHY_STYLE.test(element.getAttribute('style') ?? '')) return true;
  }

  return false;
}

/**
 * Imported descriptions carry their own headings and inline font sizes.
 * Text block presets should control the typography instead.
 * @param {HTMLElement} block
 */
function normalizeTextBlock(block) {
  if (block.dataset.textNormalized === 'true') return;

  const hasImportedStyles = hasImportedTypography(block);
  /* Custom blocks keep a merchant heading, such as the product title.
     Paragraph blocks, and any block whose HTML brought inline styles, still flatten. */
  const usesSingleType = block.classList.contains('paragraph');
  const isHeadingPreset = HEADING_PRESETS.some((preset) => block.classList.contains(preset));

  block.querySelectorAll('[style]').forEach((element) => {
    element.removeAttribute('style');
  });

  block.querySelectorAll('font').forEach((font) => {
    font.replaceWith(...font.childNodes);
  });

  if (!isHeadingPreset && (usesSingleType || hasImportedStyles)) {
    block.querySelectorAll('h1, h2, h3, h4, h5, h6').forEach((heading) => {
      const paragraph = document.createElement('p');
      paragraph.append(...heading.childNodes);
      heading.replaceWith(paragraph);
    });
  }

  block.dataset.textNormalized = 'true';
}

/**
 * @param {ParentNode} [root]
 */
function normalizeTextBlocks(root = document) {
  root.querySelectorAll('.text-block').forEach((block) => {
    if (block instanceof HTMLElement) normalizeTextBlock(block);
  });
}

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);

    if (this.classList.contains('text-block')) normalizeTextBlock(this);
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => normalizeTextBlocks(), { once: true });
} else {
  normalizeTextBlocks();
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof HTMLElement) normalizeTextBlocks(event.target);
});
